import { useId } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { Role, StaffProfileStoresItem } from '@nucleus-commerce/api-client/admin';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { STORE_ACCESS, type StoreAccess } from '../schemas/member-form-schema';

/** The slice of the invite and edit forms these fields read and write. */
export interface MemberAccessValues {
  roleKey: string;
  access: StoreAccess;
  storeIds: string[];
}

export interface MemberAccessFieldsProps {
  /** Roles the signed-in member may hand out (see `assignableRoles`). */
  roles: readonly Role[];
  /** Stores the signed-in member can see, from /me. */
  stores: readonly StaffProfileStoresItem[];
  roleLocked?: boolean;
}

/** Role select plus "all stores / selected stores" access, shared by the invite and edit dialogs. */
export function MemberAccessFields({ roles, stores, roleLocked = false }: MemberAccessFieldsProps) {
  const { t } = useTranslation('team');
  const form = useFormContext<MemberAccessValues>();
  const access = form.watch('access');
  const idPrefix = useId();

  return (
    <>
      <FormField
        control={form.control}
        name="roleKey"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('fields.role')}</FormLabel>
            <Select value={field.value} onValueChange={field.onChange} disabled={roleLocked}>
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('fields.rolePlaceholder')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.key} value={role.key}>
                    {role.name}
                    {' · '}
                    <span className="text-muted-foreground">
                      {role.permissions.includes('*')
                        ? t('role.allPermissions')
                        : t('role.permissions', { count: role.permissions.length })}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {roleLocked ? <FormDescription>{t('fields.roleLocked')}</FormDescription> : null}
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="access"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('fields.access')}</FormLabel>
            <FormControl>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="grid gap-2 sm:grid-cols-2"
              >
                {STORE_ACCESS.map((option) => (
                  <label
                    key={option}
                    htmlFor={`${idPrefix}-${option}`}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                      field.value === option && 'border-primary bg-primary-tint/40',
                    )}
                  >
                    <RadioGroupItem
                      id={`${idPrefix}-${option}`}
                      value={option}
                      className="mt-0.5"
                    />
                    <span className="grid gap-0.5 text-sm">
                      <span className="font-medium">
                        {t(option === 'all' ? 'fields.allStores' : 'fields.selectedStores')}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {t(option === 'all' ? 'fields.allStoresHint' : 'fields.selectedStoresHint')}
                      </span>
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </FormControl>
          </FormItem>
        )}
      />
      {access === 'selected' ? (
        <FormField
          control={form.control}
          name="storeIds"
          render={({ field }) => (
            <FormItem>
              {stores.length === 0 ? (
                <FormDescription>{t('fields.noStores')}</FormDescription>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {stores.map((store) => {
                    const id = `${idPrefix}-store-${store.id}`;
                    const checked = field.value.includes(store.id);
                    return (
                      <label
                        key={store.id}
                        htmlFor={id}
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm',
                          checked && 'border-primary',
                        )}
                      >
                        <Checkbox
                          id={id}
                          checked={checked}
                          onCheckedChange={(next) =>
                            field.onChange(
                              next === true
                                ? [...field.value, store.id]
                                : field.value.filter((value) => value !== store.id),
                            )
                          }
                        />
                        <span className="grid min-w-0 gap-0.5">
                          <span className="truncate font-medium">{store.name}</span>
                          <span className="text-muted-foreground truncate text-xs">
                            {store.slug}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </>
  );
}
